import { openProfileDocument } from "../../../backend/transport";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BUTTON, POPUP_TYPE, REPORTS_COLUMN, ROUTES, TITLES } from '../../../constants';
import { SearchBarReport, TitleBar, PaginatedTable } from '../../common';
import { useLoader } from '../../../context/LoaderContext';
import { popup } from '../../../utils';
import { vendorReport, exportWipReport } from '../../../service/report';
import { Button } from '../../shared';
import { reportDataCompiler } from '../../../utils/dataCompiler/wipReportCompiler';
import { useSearchParams } from "../../../backend/navigation";
import DateRangePicker from '../../common/DateRangePicker';

export const ReportsContent = () => {
  const { setLoading } = useLoader();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [searchTerm1, setSearchTerm1] = useState(searchParams.get('lot') || '');
  const [searchTerm2, setSearchTerm2] = useState(searchParams.get('sku') || '');
  const [searchTerm3, setSearchTerm3] = useState(searchParams.get('po') || '');
  const [reportData, setReportData] = useState([]);
  const [readError, setReadError] = useState(false);
  const [activeSearchParams, setActiveSearchParams] = useState({});
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [paginationData, setPaginationData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');
  const [datePreset, setDatePreset] = useState(searchParams.get('date_preset') || '');
  const [query, setQuery] = useState(() => searchTerm1 || searchTerm2 || searchTerm3 ? {
    terms: [searchTerm1, searchTerm2, searchTerm3], page: pageNumber,
    start: startDate, end: endDate, preset: datePreset,
  } : null);
  const requestedQuery = useRef(null);
  const requestGeneration = useRef(0);
  useEffect(() => () => {
      requestGeneration.current += 1;
      requestedQuery.current = null;
  }, []);


  // Update URL params when values change
  const updateUrlParams = useCallback((params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.lot) newParams.lot = params.lot;
    if (params.sku) newParams.sku = params.sku;
    if (params.po) newParams.po = params.po;
    if (params.page) newParams.page = params.page.toString();
    if (params.start_date) newParams.start_date = params.start_date;
    if (params.end_date) newParams.end_date = params.end_date;
    if (params.date_preset) newParams.date_preset = params.date_preset;

    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  const getReport = useCallback(({ terms, page, start, end, preset }) => {
    const generation = ++requestGeneration.current;
    setReadError(false);
    const [searchTerm1, searchTerm2, searchTerm3] = terms;
    let data = {};
    if (searchTerm1.length > 0) data.lot_name = searchTerm1;
    if (searchTerm2.length > 0) data.default_code = searchTerm2;
    if (searchTerm3.length > 0) data.po_name = searchTerm3;

    // Check if all search terms are empty
    if (Object.keys(data).length === 0) {
      setReportData([]);
      setActiveSearchParams({});
      setPaginationData({});
      setSearchParams({}, { replace: true });
      setIsLoading(false);
      setLoading(false);
      return;
    }

    // Add date range if provided
    const currentStartDate = start;
    const currentEndDate = end;
    const currentPreset = preset;

    if (currentStartDate) data.start_date = currentStartDate;
    if (currentEndDate) data.end_date = currentEndDate;

    setActiveSearchParams(data);
    setIsLoading(true);

    const params = {
      page_number: page,
      ...data,
    };

    // Update URL params
    const urlParams = {
      lot: searchTerm1,
      sku: searchTerm2,
      po: searchTerm3,
      page: params.page_number,
      start_date: currentStartDate,
      end_date: currentEndDate,
    };

    // Only include date_preset if it has a value
    if (currentPreset) {
      urlParams.date_preset = currentPreset;
    }

    updateUrlParams(urlParams);

    vendorReport({ data: params })
      .then((response) => {
        if (generation !== requestGeneration.current) return;
        setLoading(false);
        if (response?.data?.status) {
          setReportData([]);
          setPaginationData({});
          popup(POPUP_TYPE.ERR, response.data.status);
        } else if (!response?.data) {
          setReportData([]);
          setPaginationData({});
          setReadError(true);
        } else {
          setReportData(response.data?.records || []);
          setPaginationData(response.data?.params || {});
        }
      })
      .catch(() => {
        if (generation !== requestGeneration.current) return;
        setReportData([]);
        setPaginationData({});
        setReadError(true);
        setLoading(false);
      })
      .finally(() => {
        if (generation === requestGeneration.current) setIsLoading(false);
      });
  }, [setLoading, setSearchParams, updateUrlParams]);

  useEffect(() => {
    // URL setters change identity after navigation. A submitted snapshot should
    // still execute only once, rather than requery on its own URL update.
    if (!query || requestedQuery.current === query) return;
    requestedQuery.current = query;
    getReport(query);
  }, [getReport, query]);

  const submitQuery = ({ terms = [searchTerm1, searchTerm2, searchTerm3], page = 1,
    start = startDate, end = endDate, preset = datePreset } = {}) => {
    setPageNumber(page);
    setQuery({ terms, page, start, end, preset });
  };

  const handlePageChange = (value) => {
    const page = typeof value === 'function' ? value(pageNumber) : value;
    if (!page || page === pageNumber) return;
    submitQuery({ page });
  };

  const handleClearSearch = (searchTermNumber) => {
    const updatedSearchTerms = {
      1: searchTermNumber === 1 ? '' : searchTerm1,
      2: searchTermNumber === 2 ? '' : searchTerm2,
      3: searchTermNumber === 3 ? '' : searchTerm3,
    };

    // Update the specific search term
    switch (searchTermNumber) {
      case 1:
        setSearchTerm1('');
        break;
      case 2:
        setSearchTerm2('');
        break;
      case 3:
        setSearchTerm3('');
        break;
      default:
        break;
    }

    // Empty searches also own a generation, so pending results cannot restore cleared rows.
    submitQuery({ terms: [updatedSearchTerms[1], updatedSearchTerms[2], updatedSearchTerms[3]] });
  };

  const handleSearch = () => {
    const trimmedLot = searchTerm1.trim();
    const trimmedSku = searchTerm2.trim();
    const trimmedPo = searchTerm3.trim();
    setSearchTerm1(trimmedLot);
    setSearchTerm2(trimmedSku);
    setSearchTerm3(trimmedPo);
    if (trimmedLot || trimmedSku || trimmedPo) {
      submitQuery({ terms: [trimmedLot, trimmedSku, trimmedPo] });
    }
  };

  const handleDateApply = ({ start, end, preset }) => {
    setStartDate(start);
    setEndDate(end);
    setDatePreset(preset || '');
    setPageNumber(1);

    submitQuery({ start, end, preset: preset || '' });
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset || '');
  };

  const handleClearFilter = () => {
    setReportData([]);
    setActiveSearchParams({});
    setSearchTerm1('');
    setSearchTerm2('');
    setSearchTerm3('');
    setPaginationData({});
    setPageNumber(1);
    setStartDate('');
    setEndDate('');
    setDatePreset('');
    setShowDateFilter(false);
    setSearchParams({}, { replace: true });
  };

  const handlePrintReport = () => {
    const data = activeSearchParams;

    setIsPrintLoading(true);
    exportWipReport({ data })
      .then((response) => {
        setIsPrintLoading(false);
        if (response?.url) {
          openProfileDocument(response.url, '_blank');
          popup(POPUP_TYPE.SUCCESS, 'WIP report exported successfully');
        } else {
          popup(POPUP_TYPE.ERR, 'Failed to export WIP report');
        }
      })
      .catch((error) => {
        setIsPrintLoading(false);
        setLoading(false);
        popup(POPUP_TYPE.ERR, error.message || 'Failed to export WIP report');
      });
  };

  return (
    <>
      {readError && <p role="alert">Unable to load WIP report. Try again.</p>}
      <TitleBar title={[{ text: TITLES.REPORTS, route: ROUTES.REPORTS }]} />
      <div className="d-flex justify-content-start gap-2 mb-2">
        <SearchBarReport
          setSearchTerm={setSearchTerm1}
          searchTerm={searchTerm1}
          placeholder={'Lot Name'}
          onClear={() => handleClearSearch(1)}
        />
        <SearchBarReport
          setSearchTerm={setSearchTerm2}
          searchTerm={searchTerm2}
          placeholder={'SKU Name'}
          onClear={() => handleClearSearch(2)}
        />
        <SearchBarReport
          setSearchTerm={setSearchTerm3}
          searchTerm={searchTerm3}
          placeholder={'PO / V. Ref'}
          onClear={() => handleClearSearch(3)}
        />
        <Button
          clickHandler={handleSearch}
          className={`search-btn ${
            (!searchTerm1 && !searchTerm2 && !searchTerm3) ||
            (!readError && searchTerm1 === (searchParams.get('lot') || '') &&
              searchTerm2 === (searchParams.get('sku') || '') &&
              searchTerm3 === (searchParams.get('po') || ''))
              ? 'disabled'
              : ''
          } d-flex align-items-center justify-content-center px-3`}
          disabled={
            (!searchTerm1 && !searchTerm2 && !searchTerm3) ||
            (!readError && searchTerm1 === (searchParams.get('lot') || '') &&
              searchTerm2 === (searchParams.get('sku') || '') &&
              searchTerm3 === (searchParams.get('po') || ''))
          }>
          {BUTTON.SEARCH}
        </Button>
        <Button
          clickHandler={handleClearFilter}
          className={`search-btn ${
            reportData?.length === 0 && !startDate && !endDate ? 'disabled' : ''
          } d-flex align-items-center justify-content-center px-3`}
          disabled={reportData?.length === 0 && !startDate && !endDate}>
          {BUTTON.CLEAR_FILTERS}
        </Button>
        <Button
          clickHandler={handlePrintReport}
          className={`search-btn ${
            reportData?.length === 0 || isPrintLoading ? 'disabled' : ''
          } d-flex align-items-center justify-content-center px-3`}
          disabled={reportData?.length === 0 || isPrintLoading}>
          {isPrintLoading && (
            <span className="spinner-border spinner-border-sm text-white me-2" role="status" />
          )}
          {isPrintLoading ? 'Printing...' : BUTTON.PRINT}
        </Button>
      </div>

      <div className="d-flex align-items-center gap-3 mb-3">
        <Button className="blue-btn px-3" clickHandler={() => setShowDateFilter(!showDateFilter)}>
          {showDateFilter ? 'Hide Date Range' : 'Apply Date Range'}
        </Button>
      </div>

      <div className={`collapse ${showDateFilter ? 'show' : ''} mb-3`}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          preset={datePreset}
          onApply={handleDateApply}
          onPresetChange={handlePresetChange}
        />
      </div>

      {reportData.length ? (
        <PaginatedTable
          columns={REPORTS_COLUMN}
          initialData={reportData}
          colFilter={true}
          compiler={{ use: true, tool: reportDataCompiler }}
          pageNumber={pageNumber}
          setPageNumber={handlePageChange}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      ) : !readError && (
        <div>
          <h4 className="text-center px-1 py-5">No data available, Please search..!!</h4>
        </div>
      )}
    </>
  );
};
